<template>
  <Section
    :id="id"
    class="stop-entry-section entry-section"
    :header="t('shipments.stops', 2)"
  >
    <template v-if="addable" #headerRight>
      <RygenButton
        mode="tertiary"
        :label="t('multistop.rearrangeStops')"
        icon="pi pi-sort-alt"
        data-testid="stop-entry-top-rearrange-stops-button"
        @click="isRearrangeStopsModalVisible = true"
      />
    </template>

    <template #default>
      <ul role="list" class="stop-entry-section-list">
        <li v-for="(stop, index) in internalValue" :key="index">
          <StopViewEdit
            :ref="(el) => (stopViewEdits[index] = el as StopViewEditComponent)"
            :model-value="stop"
            :stops="internalValue"
            :mileages="mileage"
            :error-response="serverErrorsArray[index]"
            :has-next-stop="index < internalValue.length - 1"
            :allow-changing-stop-type="addable"
            :removable="internalValue.length > 2"
            :show-order-reference-numbers="showOrderReferenceNumbers"
            @remove="onRemove(index)"
            @update:edit-mode="onUpdateEditMode(index, $event)"
            @update:model-value="onUpdate(index, $event)"
          />
        </li>
      </ul>

      <InputError
        v-for="error of allErrors"
        :id="error.$uid"
        :key="error.$uid"
        :error="error"
      />
    </template>

    <template v-if="addable" #footer>
      <AddButton
        :label="t('shipments.pickups')"
        data-testid="stop-entry-add-pickup-button"
        @click="onAdd(ShipmentStopType.PICKUP)"
      />

      <AddButton
        :label="t('shipments.dropoffs')"
        data-testid="stop-entry-add-dropoff-button"
        @click="onAdd(ShipmentStopType.DROP_OFF)"
      />

      <RygenButton
        mode="tertiary"
        :label="t('multistop.rearrangeStops')"
        icon="pi pi-sort-alt"
        data-testid="stop-entry-bottom-rearrange-stops-button"
        @click="isRearrangeStopsModalVisible = true"
      />

      <MultistopArrangeableStopListModal
        v-model:visible="isRearrangeStopsModalVisible"
        :model-value="shipment"
        class="max-w-full"
        @update:model-value="onRearrangeStopsConfirm"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useScrollAnimations } from '@rygen/rygen-ui/composables';
import { ErrorResponse, Loadable } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  Order,
  Shipment,
  ShipmentStop,
  ShipmentStopType,
  StopMileageResponse,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { getFieldErrors } from '@/util';

import StopViewEdit from '@/components/entry/StopViewEdit.vue';
import MultistopArrangeableStopListModal from '@/components/location/MultistopArrangeableStopListModal.vue';
import AddButton from '@/components/ui/AddButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputError from '@rygen/rygen-ui/components/form/InputError.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

type StopViewEditComponent = InstanceType<typeof StopViewEdit>;

/**
 * These are entities that a user can submit
 *
 * This component doesn't support stop types from other entities
 */
export type SubmittableParentEntity = Order | Shipment;
export type StopArray = SubmittableParentEntity['stops'];
export type Stop = StopArray[0];

const props = defineProps({
  modelValue: {
    type: Array as PropType<StopArray>,
    default: () => [],
  },
  id: {
    type: String,
    required: true,
  },
  /** An array of vuelidate errors for the stop array as a whole. */
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  /**
   * A mapping of server errors for each stop
   *
   * The keys have the form `stops[{index}].{field}`, where `index` is the index
   * of the stop and `field` is the `ShipmentStop` field that has the validation
   * errors.
   * */
  serverErrors: {
    type: Object as PropType<ErrorResponse>,
    default: () => undefined,
  },
  addable: {
    type: Boolean,
    default: () => false,
  },
  showOrderReferenceNumbers: {
    type: Boolean,
    default: () => false,
  },
});
const {
  modelValue,
  id,
  errors,
  serverErrors,
  addable,
  showOrderReferenceNumbers,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'rearrange', sequenceNumberMappings: Record<number, number>): void;
  (event: 'update:model-value', modelValue: StopArray): void;
}>();

const { t } = useI18n();
const { parentEntity } = useParentEntityStore();
const { scrollIntoView } = useScrollAnimations();

const internalValue: Ref<StopArray> = ref([]);
const isRearrangeStopsModalVisible = ref(false);
const mileage: Ref<Loadable<StopMileageResponse[]>> = ref({
  loading: false,
});
const mileageCancelToken: Ref<CancelTokenSource | undefined> = ref();
const editingIndex = ref(0);
const stopViewEdits: Ref<Array<StopViewEditComponent | null>> = ref([]);

const shipment = computed((): Shipment => {
  return parentEntity.value instanceof Shipment
    ? parentEntity.value
    : new Shipment();
});

const serverErrorsArray = computed((): Array<ErrorResponse | undefined> => {
  if (!serverErrors?.value?.errorDetails?.fieldErrors) {
    return [];
  }

  // Split the single ErrorResponse into an ErrorResponse for each stop
  const entries = Object.entries(serverErrors.value.errorDetails.fieldErrors);
  return internalValue.value.map((_stop, index) => {
    const errorsForStop = entries.filter(([field]) =>
      field.includes(`stops[${index}]`),
    );
    return serverErrors.value && errorsForStop.length
      ? {
          ...serverErrors.value,
          errorDetails: {
            fieldErrors: Object.fromEntries(errorsForStop),
          },
        }
      : undefined;
  });
});

const allErrors = computed(() => {
  const serverErrors = serverErrorsArray.value.filter(notNull);
  const numStopsWithValidationErrors = serverErrors.length;
  if (!numStopsWithValidationErrors) {
    return errors.value;
  }

  const stopSpecificErrors = serverErrors
    .map((e) => {
      const fieldErrors = getFieldErrors(e);

      // Validation errors only apply to one field at a time.
      const [propertyPath, errorMessages] = Object.entries(fieldErrors)[0];
      if (!propertyPath) {
        return;
      }

      return {
        $uid: e.spanId,
        $propertyPath: propertyPath,
        $property: 'stops',
        $message: errorMessages.join(';'),
        $validator: 'API',
        $params: {},
        $response: undefined,
        $pending: false,
      };
    })
    .filter(notNull);
  return [...stopSpecificErrors, ...errors.value];
});

const onAdd = async (stopType?: ShipmentStopType) => {
  // Orders/Shipments always have at least two stops so the array should never
  // be empty
  const Clazz = Object.getPrototypeOf(internalValue.value[0]).constructor;
  internalValue.value.push(
    new Clazz({
      sequenceNumber: internalValue.value.length + 1,
      stopType,
    }),
  );
  editingIndex.value = internalValue.value.length - 1;
  nextTick(() => {
    const newStopViewEdit = stopViewEdits.value[editingIndex.value];
    scrollIntoView(newStopViewEdit?.$el);
  });
};

const onRemove = (i: number) => {
  internalValue.value.splice(i, 1);
  stopViewEdits.value.splice(i, 1);

  if (editingIndex.value === i) {
    editingIndex.value = -1;
  }
};

const onUpdate = (index: number, stop: StopArray[number]) => {
  // @ts-ignore `stop` is coming from `internalValue` so we know its the right type
  internalValue.value.splice(index, 1, stop);
};

const onUpdateEditMode = (index: number, isInEditMode: boolean) => {
  // No stop being open is acceptable
  editingIndex.value = isInEditMode ? index : -1;
  if (
    isInEditMode &&
    editingIndex.value >= 0 &&
    editingIndex.value < internalValue.value.length
  ) {
    nextTick(() => {
      const newStopViewEdit = stopViewEdits.value[editingIndex.value];
      scrollIntoView(newStopViewEdit?.$el);
    });
  }
};

const reset = async () => {
  if (internalValue.value.length === 0) {
    const Clazz = parentEntity.value?.stops?.[0]
      ? Object.getPrototypeOf(parentEntity.value.stops[0]).constructor
      : ShipmentStop;
    internalValue.value.push(new Clazz({ sequenceNumber: 1 }));
  }
  editingIndex.value = 0;
};

const findMileage = async () => {
  if (mileageCancelToken.value) {
    findMileageDebounced.cancel();
    mileageCancelToken.value.cancel();
  }
  mileageCancelToken.value = axios.CancelToken.source();

  const areAllAddressesEntered = internalValue.value
    .map((s) => s.location)
    .every(
      (l) =>
        !!l.addressLine1 &&
        !!l.city &&
        !!l.state &&
        !!l.postalCode &&
        !!l.country,
    );
  if (!areAllAddressesEntered || internalValue.value.length < 2) {
    mileage.value = { loading: false };
    return;
  }

  try {
    mileage.value.loading = true;
    mileage.value.data = await api.route.findMileage(
      internalValue.value,
      mileageCancelToken.value,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching mileage', e);
    mileage.value.error = e;
  } finally {
    mileage.value.loading = false;
  }
};
const findMileageDebounced = debounce(findMileage, 1000);

const onRearrangeStopsConfirm = (newShipment: Shipment) => {
  // Stop rearranging is only available for shipments, not orders
  const oldStops = internalValue.value as ShipmentStop[];
  const newStops = newShipment.stops;

  const sequenceNumberPairs = oldStops.map((oldStop) => {
    const newStop = newStops.find((newStop) => newStop.uuid === oldStop.uuid);
    if (!newStop) {
      throw new Error(
        `Error finding position of stop ${oldStop.sequenceNumber} after rearranging`,
      );
    }

    return [oldStop.sequenceNumber!, newStop.sequenceNumber!];
  });
  const sequenceNumberMappings: Record<number, number> =
    Object.fromEntries(sequenceNumberPairs);

  internalValue.value = newStops;
  emit('rearrange', sequenceNumberMappings);
};

watch(
  modelValue,
  (newVal: StopArray) => {
    internalValue.value = newVal.map((s) => {
      const Clazz = Object.getPrototypeOf(s).constructor;
      return new Clazz(s);
    });
  },
  { deep: true, immediate: true },
);

watch(
  internalValue,
  (newVal: StopArray) => {
    if (!isEqual(modelValue.value, newVal)) {
      emit('update:model-value', newVal);
      findMileageDebounced();
    }
  },
  { deep: true },
);

defineExpose({
  reset,
});
</script>

<style>
.stop-entry-section {
  --location-tracker-icon-margin-top: var(
    --location-card-top-row-cell-padding-top
  );

  .section-data {
    display: flex;
    gap: var(--gap-1);
    flex-direction: column;
    align-items: start;

    &:has(.location-tracker) {
      /*
      negative margin-left equal to location tracker line and gap between
      location tracker and stop card, so that stop card lines up with rest of
      page
      */
      margin-left: calc(-1 * (var(--location-tracker-width)));

      & > .input-error {
        margin-left: var(--location-tracker-width);
      }
    }

    .stop-entry-section-list {
      display: flex;
      flex-direction: column;
      gap: var(--gap-1);
      width: 100%;
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }

  .section-footer {
    display: flex;
    align-items: center;
    gap: var(--gap-1);
    margin-top: var(--gap-1);
  }
}
</style>
